import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Container, Spinner } from "react-bootstrap";
import Select, { MultiValue, SingleValue } from "react-select";
import { UserService } from "../../../service/users.service";
import { User, rolesMap } from "../../../service/types/types";
import { useParams } from "react-router-dom";

type Option = { value: string; label: string };

const roleOptions: Option[] = Object.entries(rolesMap).map(([key, label]) => ({ value: key, label }));

export default function UpdateUserAccess() {
  const [users, setUsers] = useState<User[]>([]);
  const [usersOptions, setUsersOptions] = useState<Option[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Option[]>([]);
  const [isUserPreselected, setIsUserPreselected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await UserService.findAll();
        setUsers(data);
        setUsersOptions(data.map((user) => ({ value: user.rut, label: `${user.name} (${user.rut})` })));
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (id && users.length > 0 && !isUserPreselected) {
      handleSelectUser({ value: id, label: "" });
      setIsUserPreselected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, users]);

  const handleSelectUser = (option: SingleValue<Option>) => {
    setSuccess(false);
    setError("");
    if (!option) {
      setSelectedUser(null);
      setSelectedRoles([]);
      return;
    }
    const user = users.find((u) => u.rut === option.value) || null;
    setSelectedUser(user);
    setSelectedRoles((user?.roles ?? []).map((role) => ({ value: role.name, label: rolesMap[role.name] ?? role.name })));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      await UserService.updateRoles(selectedUser.rut, selectedRoles.map((r) => r.value));
      setSuccess(true);
    } catch (err) {
      console.error("Error updating user roles:", err);
      setError("No se pudieron guardar los roles. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-100 min-vh-100 bg-light d-flex justify-content-center p-3">
      <Container style={{ maxWidth: "640px" }}>
        <h2 className="mb-1 text-center">Permisos de Usuario</h2>
        <p className="text-muted text-center mb-4">Busca un trabajador y asigna sus roles.</p>

        <Card className="shadow-sm mb-4 p-3">
          <Select
            options={usersOptions}
            value={
              selectedUser ? { value: selectedUser.rut, label: `${selectedUser.name} (${selectedUser.rut})` } : null
            }
            onChange={handleSelectUser}
            placeholder="Buscar usuario por nombre o RUT..."
            isSearchable
            isClearable
            isLoading={loadingUsers}
            className="w-100"
          />
        </Card>

        {selectedUser && (
          <Card className="shadow-sm p-4">
            <h5 className="mb-3">
              {selectedUser.name} <span className="text-muted">({selectedUser.rut})</span>
            </h5>

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
                Roles actualizados correctamente.
              </Alert>
            )}
            {error && <Alert variant="danger">{error}</Alert>}

            <label className="form-label">Roles</label>
            <Select
              options={roleOptions}
              value={selectedRoles}
              onChange={(options: MultiValue<Option>) => setSelectedRoles(options as Option[])}
              isMulti
              placeholder="Seleccione uno o varios roles..."
              className="mb-2"
            />
            <p className="text-muted small">
              Los roles seleccionados reemplazan por completo los roles actuales del usuario.
            </p>

            <Button variant="primary" onClick={handleSave} disabled={saving} className="w-100 mt-2">
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" /> Guardando...
                </>
              ) : (
                "Guardar Roles"
              )}
            </Button>
          </Card>
        )}
      </Container>
    </div>
  );
}
